import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { Star, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { REVIEW_COUNT } from "@/config/reviews";


const googleReviews = [
  {
    name: "Joey P.",
    text: "The transformation of our backyard was beyond our expectations. The paver patio they installed has become the centerpiece of all our family gatherings. Beautiful craftsmanship!",
    rating: 5,
  },
  {
    name: "Tommy F.",
    text: "After getting several estimates, we chose Masonry Pro and couldn't be happier. They built a stunning stone retaining wall that solved our drainage issues while adding incredible curb appeal.",
    rating: 5,
  },
  {
    name: "Holly G.",
    text: "Professional from start to finish. They replaced our crumbling concrete driveway with a beautiful stamped concrete design. Two years later, it still looks brand new!",
    rating: 5,
  },
  {
    name: "Anna S.",
    text: "Our outdoor kitchen project was complex, but this team handled every detail perfectly. From the stone countertops to the built-in grill station, everything exceeded our expectations.",
    rating: 5,
  },
  {
    name: "A.S.",
    text: "We had an old brick walkway that was uneven and dangerous. They removed it and installed beautiful bluestone pavers. Now it's both safe and gorgeous!",
    rating: 5,
  },
  {
    name: "Josh F.",
    text: "The fire pit area they created has become our favorite spot. Quality stonework, perfect placement, and they even added subtle lighting. Worth every penny!",
    rating: 5,
  },
  {
    name: "Blake B.",
    text: "Fixed our leaning retaining wall that another company had botched. They did it right with proper drainage and reinforcement. It's been three years and hasn't moved an inch.",
    rating: 5,
  },
  {
    name: "Roksolana M.",
    text: "Incredible attention to detail on our front steps and stoop. They matched the existing stonework on our 1920s home perfectly. Neighbors keep asking who did the work!",
    rating: 5,
  },
  {
    name: "Craig A.",
    text: "Had them repair tuckpointing on our chimney and it looks better than when the house was built. Fair pricing, clean work, and they finished ahead of schedule.",
    rating: 5,
  },
  {
    name: "Brianna V.",
    text: "The team installed a beautiful stone veneer on our foundation and it completely transformed our home's exterior. So many compliments from neighbors!",
    rating: 5,
  },
];

const Reviews = () => {
  return (
    <>
      <Helmet>
        <title>{`${REVIEW_COUNT}+ Reviews Totowa, NJ | Masonry Pro Construction`}</title>
        <meta name="description" content={`Rated 5.0 stars with ${REVIEW_COUNT}+ Google reviews. See what Totowa and North Jersey homeowners say about our patios, retaining walls, and masonry work firsthand.`} />
        <meta property="og:title" content={`${REVIEW_COUNT}+ Reviews Totowa, NJ | Masonry Pro Construction`} />
        <meta property="og:description" content={`Rated 5.0 stars with ${REVIEW_COUNT}+ Google reviews. See what Totowa and North Jersey homeowners say about our patios, retaining walls, and masonry work firsthand.`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/og/paver-patios.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>
      <Header />
      <main>
        {/* Hero / Header Section */}
        <section className="bg-primary pt-32 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
              What Totowa and North Jersey Homeowners Say About Masonry Pro
            </h1>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Trusted by North Jersey homeowners for over 35 years.
            </p>

            {/* Trust Bar */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-secondary text-secondary" />
                ))}
              </div>
              <span className="text-xl font-bold text-primary-foreground">5.0 Rating</span>
              <span className="text-primary-foreground/60">|</span>
              <span className="text-primary-foreground/90 font-medium">{`${REVIEW_COUNT}+ Google Reviews`}</span>
            </div>

            <p className="max-w-2xl mx-auto text-primary-foreground/70 leading-relaxed">
              Every project we take on starts with a conversation and ends with a handshake. These testimonials are from real home and business owners across Passaic, Bergen, Essex, and Morris counties who trusted us with their property.
            </p>
          </div>
        </section>

        {/* Google Reviews Grid */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-10">
              <h2 className="text-2xl font-bold text-foreground">Google Reviews</h2>
              <span className="text-sm text-muted-foreground">(via Google)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {googleReviews.map((review, index) => (
                <div
                  key={index}
                  className="bg-card rounded-xl p-6 border border-border hover:border-secondary/40 transition-colors duration-300"
                >
                  <Quote className="h-7 w-7 text-secondary/25 mb-3" />

                  <div className="flex mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-secondary text-secondary" />
                    ))}
                  </div>

                  <p className="text-foreground/90 text-sm leading-relaxed mb-5">
                    "{review.text}"
                  </p>

                  <div className="pt-4 border-t border-border">
                    <p className="font-semibold text-foreground">{review.name}</p>
                    <p className="text-xs text-muted-foreground">Google Review</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <a
                href="https://www.google.com/search?sca_esv=76156e36b6817723&sxsrf=ANbL-n5uJ9VilabAcYuAojzWe0sf2LIuRw%3A1769192938668&si=AL3DRZFIhG6pAqfNLal55wUTwygCG0fClF3UxiOmgw9Hq7nbWeKBdZjg2DfCa10rHcV60Uh0Ib9CcfHw3Yock_EyQES063vkxLITzUGLUFP7lbi2mk-aQxG86JYDjauGpI1gZ2Wkt0lzNfpUzJhTuzP7XbEdE5bJVQ%3D%3D&q=Masonry+Pro+Construction+Reviews&sa=X&ved=2ahUKEwjN25elpaKSAxUlmSYFHT3NO-4Q0bkNegQIJhAF&biw=1994&bih=976&dpr=0.9&aic=0&num=25"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-secondary hover:text-secondary/80 transition-colors font-medium"
              >
                View All Reviews on Google →
              </a>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
};

export default Reviews;
